import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  HeartHandshake, Loader2, Star, Compass, Eye, Gem, Sparkles, Megaphone,
  Trophy, Target, GraduationCap, ChevronDown, NotebookPen, Plus, Trash2,
  Lock, Share2, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";

function Field({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <Icon size={14} className="text-brand-blue mt-0.5 shrink-0" />
      <div>
        <p className="text-xs uppercase tracking-wider text-brand-muted font-semibold">{label}</p>
        <p className="text-sm text-white/90">{value}</p>
      </div>
    </div>
  );
}

function SessionManager({ consultantId }) {
  const [sessions, setSessions] = useState(null);
  const [form, setForm] = useState({ topic: "", notes: "", action_items: "", visibility: "shared" });
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = () => api.get(`/coach/sessions?consultant_id=${consultantId}`).then((r) => setSessions(r.data));
  useEffect(() => { load(); }, [consultantId]);

  const save = async () => {
    if (!form.topic.trim()) { toast.error("Indica o tema da sessão"); return; }
    setSaving(true);
    try {
      await api.post("/coach/sessions", { consultant_id: consultantId, ...form });
      toast.success("Sessão registada!");
      setForm({ topic: "", notes: "", action_items: "", visibility: "shared" });
      setAdding(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/coach/sessions/${id}`);
    toast.success("Sessão removida");
    await load();
  };

  return (
    <div className="mt-4 pt-4 border-t border-brand-border">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-heading font-semibold text-sm flex items-center gap-2"><NotebookPen size={15} className="text-brand-orange" /> Sessões & notas</h4>
        <Button size="sm" onClick={() => setAdding((a) => !a)} data-testid="add-session-toggle"
          className="bg-brand-blue hover:bg-brand-bluehover rounded-sm font-semibold h-8">
          <Plus size={14} className="mr-1" /> Nova sessão
        </Button>
      </div>

      {adding && (
        <div className="p-4 rounded-sm border border-brand-border bg-background space-y-3 mb-3">
          <div>
            <Label className="text-xs">Tema</Label>
            <Input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="session-topic"
              placeholder="Ex: Plano de prospeção" className="mt-1 bg-brand-surface border-brand-border rounded-sm h-9" />
          </div>
          <div>
            <Label className="text-xs">Notas</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="session-notes"
              rows={2} placeholder="Observações da sessão..." className="mt-1 bg-brand-surface border-brand-border rounded-sm" />
          </div>
          <div>
            <Label className="text-xs">Ações a realizar</Label>
            <Textarea value={form.action_items} onChange={(e) => setForm({ ...form, action_items: e.target.value })} data-testid="session-actions"
              rows={2} placeholder="Compromissos até à próxima sessão..." className="mt-1 bg-brand-surface border-brand-border rounded-sm" />
          </div>
          <div className="flex items-center justify-between">
            <div className="inline-flex rounded-sm border border-brand-border overflow-hidden" data-testid="session-visibility">
              {[["shared", "Partilhada", Share2], ["private", "Privada", Lock]].map(([v, l, Ic]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, visibility: v })}
                  className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${form.visibility === v ? "bg-brand-blue text-white" : "text-brand-muted hover:text-white"}`}>
                  <Ic size={12} /> {l}
                </button>
              ))}
            </div>
            <Button size="sm" onClick={save} disabled={saving} data-testid="save-session-button"
              className="bg-brand-orange hover:bg-brand-orangehover rounded-sm font-bold h-8">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          </div>
          <p className="text-xs text-brand-muted">Sessões partilhadas ficam visíveis para o consultor. Privadas são só tuas.</p>
        </div>
      )}

      {sessions === null ? (
        <Loader2 className="h-5 w-5 animate-spin text-brand-blue" />
      ) : sessions.length === 0 ? (
        <p className="text-sm text-brand-muted">Ainda sem sessões registadas.</p>
      ) : (
        <div className="space-y-2">
          {sessions.map((s) => (
            <div key={s.id} data-testid="session-item" className="p-3 rounded-sm border border-brand-border">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-sm">{s.topic}</p>
                  <p className="text-xs text-brand-muted flex items-center gap-1"><CalendarDays size={12} /> {new Date(s.date).toLocaleDateString("pt-PT")}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-sm flex items-center gap-1 ${s.visibility === "shared" ? "bg-brand-success/15 text-brand-success" : "bg-brand-border/60 text-brand-muted"}`}>
                    {s.visibility === "shared" ? <Share2 size={11} /> : <Lock size={11} />}{s.visibility === "shared" ? "Partilhada" : "Privada"}
                  </span>
                  <button onClick={() => remove(s.id)} className="text-brand-muted hover:text-destructive" data-testid="delete-session"><Trash2 size={14} /></button>
                </div>
              </div>
              {s.notes && <p className="text-sm text-white/85 mt-2">{s.notes}</p>}
              {s.action_items && <p className="text-xs text-brand-orange mt-1">Ações: {s.action_items}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CoachPanel() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState({});

  useEffect(() => { api.get("/coach/consultants").then((r) => setList(r.data)).catch(() => {}); }, []);

  if (!list) return <div className="h-96 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-blue" /></div>;

  return (
    <div className="space-y-6" data-testid="coach-panel">
      <div>
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-brand-orange">Coaching</span>
        <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2 mt-1">
          <HeartHandshake className="text-brand-orange" /> Consultores acompanhados
        </h1>
        <p className="text-brand-muted text-sm mt-1">Perfis partilhados contigo. Regista sessões e notas de acompanhamento.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {list.map((c) => {
          const p = c.profile || {};
          const isOpen = open[c.id];
          return (
            <div key={c.id} data-testid="coached-consultant" className="rounded-sm bg-brand-surface border border-brand-border overflow-hidden">
              <button onClick={() => setOpen((o) => ({ ...o, [c.id]: !o[c.id] }))}
                className="w-full p-5 flex items-center gap-3 text-left">
                <div className="h-11 w-11 rounded-sm bg-gradient-to-br from-brand-blue to-brand-orange flex items-center justify-center font-bold text-white shrink-0">{c.name[0]}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{c.name}</p>
                  <p className="text-xs text-brand-muted truncate">{c.agency || "—"} · {p.mbti || "MBTI n/d"}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-heading font-bold text-brand-orange">{c.points}</p>
                  <p className="text-xs text-brand-muted">{c.level_name}</p>
                </div>
                <ChevronDown size={18} className={`text-brand-muted transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>

              {isOpen && (
                <div className="px-5 pb-5 border-t border-brand-border pt-4">
                  <div className="flex gap-4 text-xs text-brand-muted mb-3">
                    <span className="flex items-center gap-1"><Trophy size={13} className="text-brand-orange" /> {c.points} pts</span>
                    <span className="flex items-center gap-1"><Target size={13} className="text-brand-blue" /> {c.goals_completed} objetivos</span>
                    <span className="flex items-center gap-1"><GraduationCap size={13} className="text-brand-success" /> {c.modules_completed} módulos</span>
                  </div>
                  <div className="space-y-3">
                    <Field icon={Star} label="Personalidade (MBTI)" value={p.mbti} />
                    <Field icon={Compass} label="Missão" value={p.mission} />
                    <Field icon={Eye} label="Visão" value={p.vision} />
                    <Field icon={Gem} label="Valores" value={p.values} />
                    <Field icon={Sparkles} label="Ideal Pessoal" value={p.personal_ideal} />
                    <Field icon={Megaphone} label="Proposta de Valor" value={p.value_proposition} />
                  </div>
                  <SessionManager consultantId={c.id} />
                </div>
              )}
            </div>
          );
        })}
        {list.length === 0 && (
          <div className="col-span-full text-center py-16 text-brand-muted">
            <HeartHandshake size={40} className="mx-auto mb-3 opacity-40" />
            Ainda nenhum consultor partilhou o perfil contigo.
          </div>
        )}
      </div>
    </div>
  );
}
